// Job Detail Content Component - Shared between Jobs and PublicJobs screens
import React from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { Clock, MapPin, Stethoscope, Target, Calendar, Navigation } from 'lucide-react';
import moment from 'moment';
import { webColors } from '../../../constants/webStyles';
import { jobTypes } from '../../../constants/lov/professions';
import { formatPrice, formatPriceRange } from '../../../utils/currency';
import { jobDetailStyles as styles } from './jobStyles';
import { getColorFromName, getTextColorFromName, getJobTypeBgColor, getJobTypeColor } from './jobHelpers';
import { professionLabels } from './jobConstants';
import { useIsMobile } from '../../../hooks/useIsMobile';
import JobShareButton from './JobShareButton';

/**
 * Job Detail Content Component (Right Panel)
 * @param {Object} props
 * @param {Object} props.job - Detailed job data object
 * @param {Array} [props.jobFacilityData] - Facility/expertise data
 * @param {boolean} props.isFullTime - Whether this is a full-time job
 * @param {number} props.diffHours - Number of hours for shift (locum)
 * @param {string} props.displayCurrency - Currency code to display prices in
 * @param {boolean} [props.isFavorited] - Whether job is favorited (optional, for authenticated users)
 * @param {Function} props.onApply - Callback when Apply button is pressed
 * @param {Function} [props.onToggleFavorite] - Callback when Save/Unsave button is pressed (optional)
 * @param {string} [props.shareUrl] - URL to share (defaults to current page URL)
 */
const JobDetailContent = ({
  job,
  jobFacilityData,
  isFullTime,
  diffHours,
  displayCurrency,
  isFavorited,
  onApply,
  onToggleFavorite,
  shareUrl
}) => {
  const isMobile = useIsMobile();
  if (!job) return null;

  const isInternational = job.type === 4 || job.type === '4' || !!job.international;
  const currency = isInternational ? 'USD' : displayCurrency;

  const getJobType = () => {
    if (job.international) return 'International';
    if (job.type !== undefined && job.type !== null) {
      const typeObj = jobTypes.find(t => t.id === parseInt(job.type));
      return typeObj?.name || 'Locum';
    }
    return 'Locum';
  };

  const clinicName = job.clinic?.name;
  const location = job.clinic?.city
    ? `${job.clinic.city}${job.clinic?.state ? `, ${job.clinic.state}` : ''}`
    : (job.location || 'Location TBD');

  // Check if save functionality is available (authenticated mode)
  const hasSaveFeature = typeof onToggleFavorite === 'function';

  const formatSchedule = () => {
    if (isFullTime || !job.start_timestamp || !job.end_timestamp) return null;
    const start = moment(job.start_timestamp);
    const end = moment(job.end_timestamp);
    const dayDate = start.format('ddd, D MMM');
    const startTime = start.format('h:mmA').replace(':00', '');
    const endTime = end.format('h:mmA').replace(':00', '');
    return `${dayDate} • ${startTime} – ${endTime}`;
  };

  return (
    <View style={styles.detailContent}>
      {/* Header Section */}
      <View style={[styles.detailHeader, isMobile && styles.detailHeaderMobile]}>
        <View style={[styles.detailLogo, { backgroundColor: getColorFromName(clinicName) }]}>
          <Text style={styles.detailLogoText}>{clinicName?.[0] || 'C'}</Text>
        </View>
        <View style={styles.detailHeaderInfo}>
          <Text style={[styles.detailCompanyName, { color: getTextColorFromName(clinicName) }]}>
            {clinicName || 'Healthcare Provider'}
          </Text>
          {job.subtitle && (
            <View style={styles.detailSubtitlePill}>
              <Text style={styles.detailSubtitleText}>{job.subtitle}</Text>
            </View>
          )}
          <Text style={styles.detailJobTitle}>{job.title}</Text>
          <View style={styles.detailMeta}>
            <View style={styles.detailMetaItemRow}>
              <Clock size={14} color={webColors.textMuted} />
              <Text style={styles.detailMetaItem}>Posted {moment(job.created_at).fromNow()}</Text>
            </View>
            <View style={styles.detailMetaItemRow}>
              <MapPin size={14} color={webColors.textMuted} />
              <Text style={styles.detailMetaItem}>{location}</Text>
            </View>
            <View style={styles.detailMetaItemRow}>
              <Text style={styles.detailMetaItem}>{formatPrice(job.price, currency)}</Text>
            </View>
          </View>

          {/* Action Buttons */}
          <View style={styles.detailActions}>
            <TouchableOpacity style={styles.applyBtn} onPress={onApply}>
              <Text style={styles.applyBtnText}>Apply Now</Text>
            </TouchableOpacity>
            {hasSaveFeature && (
              <TouchableOpacity style={styles.saveBtn} onPress={onToggleFavorite}>
                <Text style={styles.saveBtnText}>{isFavorited ? '★ Saved' : 'Save'}</Text>
              </TouchableOpacity>
            )}
            {/* Share Button */}
            <JobShareButton job={job} shareUrl={shareUrl} />
          </View>

          {/* Tags */}
          <View style={styles.detailTags}>
            <View style={[styles.detailTag, { backgroundColor: getJobTypeBgColor(getJobType()) }]}>
              <Text style={[styles.detailTagText, { color: getJobTypeColor(getJobType()) }]}>
                {getJobType()}
              </Text>
            </View>
            {formatSchedule() && (
              <View style={[styles.detailTag, styles.detailTagRow, styles.detailTagSchedule]}>
                <Calendar size={13} color="#c2185b" />
                <Text style={[styles.detailTagText, styles.detailTagScheduleText]}>{formatSchedule()}</Text>
              </View>
            )}
            <View style={[styles.detailTag, styles.detailTagRow]}>
              <Stethoscope size={13} color="#4b5563" />
              <Text style={styles.detailTagText}>
                {professionLabels[job.profession] || 'Healthcare'}
              </Text>
            </View>
            <View style={[styles.detailTag, styles.detailTagRow]}>
              <Target size={13} color="#4b5563" />
              <Text style={styles.detailTagText}>{job.specialty || 'General'}</Text>
            </View>
            <View style={styles.detailTag}>
              <Text style={styles.detailTagText}>{job.work_mode || 'Onsite'}</Text>
            </View>
          </View>
        </View>
      </View>

      {/* About the job */}
      <View style={styles.detailSection}>
        <Text style={styles.detailSectionTitle}>About the job</Text>
        <Text style={styles.detailSectionText}>{job.description || 'No description provided.'}</Text>
      </View>

      {/* Earnings Section (for locum) */}
      {!isFullTime && (
        <View style={styles.detailSection}>
          <Text style={styles.detailSectionTitle}>Earnings</Text>
          <Text style={styles.earningsHighlight}>
            {formatPrice(job.rate_per_hour || (job.price / diffHours).toFixed(2), currency)} per hour
          </Text>
          <Text style={styles.earningsTotal}>
            Total: {formatPrice(job.price, currency)} ({diffHours} hours)
          </Text>
          <View style={[styles.paymentBadge, { borderColor: !job.is_cash ? '#FC6767' : '#6b7280' }]}>
            <Text style={[styles.paymentBadgeText, { color: !job.is_cash ? '#FC6767' : '#6b7280' }]}>
              {job.is_cash ? 'CASH' : 'S-PAY'}
            </Text>
          </View>
        </View>
      )}

      {/* Salary Section (for full-time) */}
      {isFullTime && job.price > 0 && (
        <View style={styles.detailSection}>
          <Text style={styles.detailSectionTitle}>Salary</Text>
          <Text style={styles.salaryText}>
            {formatPriceRange(job.price, job.price_max, currency)} per month
          </Text>
        </View>
      )}

      {/* Clinical Expertise (for locum) */}
      {!isFullTime && jobFacilityData?.length > 0 && (
        <View style={styles.detailSection}>
          <Text style={styles.detailSectionTitle}>Clinical Expertise Required</Text>
          {jobFacilityData.filter(val => val.category === 'GP').length > 0 && (
            <View style={styles.expertiseGroup}>
              <Text style={styles.expertiseLabel}>GENERAL PRACTITIONER</Text>
              {jobFacilityData.filter(val => val.category === 'GP').map((item, idx) => (
                <View key={idx} style={styles.listItem}>
                  <Text style={styles.listBullet}>•</Text>
                  <Text style={styles.listText}>{item.doctor_title}</Text>
                </View>
              ))}
            </View>
          )}
          {jobFacilityData.filter(val => val.category !== 'GP').length > 0 && (
            <View style={styles.expertiseGroup}>
              <Text style={styles.expertiseLabel}>SPECIALIST</Text>
              {jobFacilityData.filter(val => val.category !== 'GP').map((item, idx) => (
                <View key={idx} style={styles.listItem}>
                  <Text style={styles.listBullet}>•</Text>
                  <Text style={styles.listText}>{item.doctor_title}</Text>
                </View>
              ))}
            </View>
          )}
        </View>
      )}

      {/* Date & Time */}
      <View style={styles.detailSection}>
        <Text style={styles.detailSectionTitle}>
          {isFullTime ? 'Application Deadline' : 'Date & Time'}
        </Text>
        <Text style={styles.dateText}>
          {moment(job.start_timestamp).format('dddd, MMMM Do YYYY')}
        </Text>
        {!isFullTime && (
          <Text style={styles.timeText}>
            {moment(job.start_timestamp).format('h:mm A')} ~ {moment(job.end_timestamp).format('h:mm A')}
          </Text>
        )}
      </View>

      {/* Facility Info */}
      <View style={styles.detailSection}>
        <Text style={styles.detailSectionTitle}>Facility</Text>
        <View style={styles.facilityInfo}>
          <View style={[styles.facilityLogo, { backgroundColor: getColorFromName(clinicName) }]}>
            <Text style={styles.facilityLogoText}>{clinicName?.[0] || 'C'}</Text>
          </View>
          <View style={styles.facilityDetails}>
            <Text style={styles.facilityName}>{clinicName}</Text>
            {job.clinic?.address && (
              <Text style={styles.facilityAddress}>{job.clinic.address}</Text>
            )}
            <Text style={styles.facilityAddress}>
              {[job.clinic?.city, job.clinic?.state].filter(Boolean).join(', ')}{job.clinic?.postcode ? ` ${job.clinic.postcode}` : ''}
            </Text>
            {job.contact_person ? (
              <Text style={styles.facilityAddress}>
                Person in-charge: {job.contact_person}
              </Text>
            ) : null}
          </View>
        </View>
        {(() => {
          const fullAddress = [job.clinic?.name, job.clinic?.address, job.clinic?.city, job.clinic?.state, job.clinic?.postcode].filter(Boolean).join(', ');
          if (!fullAddress) return null;
          const encodedAddress = encodeURIComponent(fullAddress);
          return (
            <View style={styles.facilityNavButtons}>
              <TouchableOpacity
                style={styles.facilityNavButton}
                onPress={() => {
                  if (typeof window !== 'undefined') {
                    window.open(
                      `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`,
                      '_blank'
                    );
                  }
                }}
              >
                <MapPin size={14} color={webColors.textPrimary} />
                <Text style={styles.facilityNavButtonText}>Open in Google Maps</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.facilityNavButton}
                onPress={() => {
                  if (typeof window !== 'undefined') {
                    window.open(
                      `https://waze.com/ul?q=${encodedAddress}&navigate=yes`,
                      '_blank'
                    );
                  }
                }}
              >
                <Navigation size={14} color={webColors.textPrimary} />
                <Text style={styles.facilityNavButtonText}>Open in Waze</Text>
              </TouchableOpacity>
            </View>
          );
        })()}
      </View>

      {/* Notes */}
      {job.note && (
        <View style={styles.detailSection}>
          <Text style={styles.detailSectionTitle}>Additional Notes</Text>
          <Text style={styles.detailSectionText}>{job.note}</Text>
        </View>
      )}
    </View>
  );
};

export default JobDetailContent;
